import React from 'react';
import { Modal, Button, TooltipHelp, Counter, Form, CheckboxLabel, ToastContext } from '@weezevent/nacre';
import { useTranslation } from 'react-i18next';
import Styles from '../../../styles/rate/modalQuotaEvent.module.css';
import { fetcher, useForm } from '@weezevent/weezjs-core';
import PropTypes from 'prop-types';

export function ModalQuotaEvent({ showModal, event, organization, settings, quotaGlobal, onSuccess, onCloseModal }) {
    const { t } = useTranslation();
    const toast = React.useContext(ToastContext);

    const validateQuota = React.useCallback(
        (name, value, form) => {
            if (value < quotaGlobal.quota_taken && value !== 0) {
                return t('ticket.rates.modalQuotaEvent.quotaExceed');
            }
            return true;
        },
        [quotaGlobal]
    );

    const settingsData = React.useMemo(() => {
        const is_unlimited = !Number(settings.is_limited_quota);

        return {
            defaultQuota: is_unlimited ? 0 : parseInt(settings.quota),
            defaultValue: is_unlimited ? true : false
        };
    }, [settings]);

    const [formQuota, setFormQuota, setFormObjectQuota, formErrors, quotaValidate] = useForm(
        {
            quota: settingsData.defaultQuota,
            unlimitedQuota: settingsData.defaultValue
        },
        {
            quota: validateQuota
        }
    );

    const handleClickToggle = React.useCallback(
        checked => {
            if (checked) {
                setFormQuota('quota', 0);
            }
            setFormQuota('unlimitedQuota', checked);
        },
        [setFormQuota]
    );

    const reset = React.useCallback(() => {
        setFormObjectQuota({
            ...formQuota,
            quota: settingsData.defaultQuota,
            unlimitedQuota: settingsData.defaultValue
        });
    }, [setFormObjectQuota, settingsData]);

    const handleSubmit = React.useCallback(() => {
        if (!quotaValidate()) {
            return;
        }

        let payload = {
            quota: formQuota.quota,
            is_limited_quota: Number(!formQuota.unlimitedQuota)
        };

        fetcher
            .patch(`/ticket/organizations/${organization.id}/events/${event.id}/settings`, { data: payload })
            .then(() => {
                onSuccess();
                onCloseModal();
                toast.success(t('ticket.rates.modalQuotaEvent.success'));
            })
            .catch(e => {
                toast.error(t('ticket.rates.modalQuotaEvent.error'));
            });
    }, [organization, event, formQuota, settings]);

    return (
        <Modal
            open={showModal}
            onClose={() => {
                reset();
                onCloseModal();
            }}
            title={t('ticket.rates.modalQuotaEvent.title')}
            subtitle={t('ticket.rates.modalQuotaEvent.description')}
            allowExternalClosing={true}
            submitButton={<Button primary onClick={handleSubmit} disabled={formErrors['quota']} label={t('ticket.common.action.save')} />}
            cancelButton={
                <Button
                    onClick={() => {
                        reset();
                        onCloseModal();
                    }}
                    label={t(`ticket.common.action.cancel`)}
                />
            }
        >
            <div className={Styles['modal-quota-event-section']}>
                <Form.Field style={{ marginTop: '0' }}>
                    <div style={{ display: 'flex', alignItems: 'baseline' }}>
                        <Form.Label label={t(`ticket.rates.infos.quantity`)} />
                        <TooltipHelp mini={false} title={t('ticket.rates.modalQuotaEvent.tooltip')} />
                    </div>
                    <div className={Styles['modal-quota-event-container']}>
                        <Counter
                            errors={formErrors['quota'] ? true : false}
                            max={1000000}
                            style={{ maxWidth: '140px', marginRight: '15px' }}
                            placeholder={0}
                            onChange={(event, value) => setFormQuota('quota', value)}
                            value={formQuota.unlimitedQuota ? 0 : formQuota.quota}
                            min={0}
                            step={1}
                            disabled={formQuota.unlimitedQuota}
                        />
                        <CheckboxLabel
                            checked={formQuota.unlimitedQuota}
                            onChange={(e, { checked }) => handleClickToggle(checked)}
                            label={t(`ticket.rates.infos.unlimitedQuantity`)}
                            name={'unlimited_quantity'}
                        />
                    </div>

                    <Form.FieldErrors errors={[formErrors['quota'] ? formErrors['quota'] : null]} />
                </Form.Field>
            </div>
        </Modal>
    );
}

ModalQuotaEvent.propTypes = {
    event: PropTypes.object.isRequired,
    organization: PropTypes.object.isRequired,
    settings: PropTypes.object.isRequired,
    showModal: PropTypes.bool.isRequired,
    onCloseModal: PropTypes.func.isRequired,
    onSuccess: PropTypes.func.isRequired
};
